/**
 * Redis storage adapter for Express
 * Provides persistent storage for production environments
 */

import type { 
  StorageAdapter, 
  AgentDetectionEvent, 
  AgentSession 
} from './types';

interface RedisClient {
  set(key: string, value: any, options?: { ex?: number }): Promise<string>;
  get(key: string): Promise<any>;
  zadd(key: string, ...args: any[]): Promise<number>;
  zrange(key: string, start: number, stop: number): Promise<string[]>;
  zrevrange(key: string, start: number, stop: number): Promise<string[]>;
  expire(key: string, seconds: number): Promise<number>;
  del(key: string): Promise<number>;
  keys(pattern: string): Promise<string[]>;
}

export class RedisStorageAdapter implements StorageAdapter {
  private prefix = 'agentshield:';
  private ttl: number;
  
  constructor(
    private redis: RedisClient,
    ttl: number = 86400 // 24 hours default
  ) {
    this.ttl = ttl;
  }
  
  private getKey(type: string, id: string): string {
    return `${this.prefix}${type}:${id}`;
  }
  
  async storeEvent(event: AgentDetectionEvent): Promise<void> {
    const eventKey = this.getKey('event', event.eventId);
    const sessionEventsKey = this.getKey('session_events', event.sessionId);
    const timelineKey = `${this.prefix}timeline`;
    
    // Store event data
    await this.redis.set(eventKey, JSON.stringify(event), { ex: this.ttl });
    
    // Add to session events (sorted set)
    const timestamp = new Date(event.timestamp).getTime();
    await this.redis.zadd(sessionEventsKey, timestamp, event.eventId);
    await this.redis.expire(sessionEventsKey, this.ttl);
    
    // Add to global timeline
    await this.redis.zadd(timelineKey, timestamp, event.eventId);
    
    // Trim timeline to keep only recent events
    const timelineCutoff = Date.now() - (this.ttl * 1000);
    await this.redis.zadd(timelineKey, timelineCutoff, 'cleanup');
  }
  
  async storeSession(session: AgentSession): Promise<void> {
    const sessionKey = this.getKey('session', session.sessionId);
    const activeSessionsKey = `${this.prefix}active_sessions`;
    
    // Store session data
    await this.redis.set(sessionKey, JSON.stringify(session), { ex: this.ttl });
    
    // Add to active sessions sorted set
    const timestamp = new Date(session.lastSeen).getTime();
    await this.redis.zadd(activeSessionsKey, timestamp, session.sessionId);
  }
  
  async getEvents(sessionId: string, limit?: number): Promise<AgentDetectionEvent[]> {
    const sessionEventsKey = this.getKey('session_events', sessionId);
    
    // Get event IDs from sorted set (most recent first)
    const eventIds = await this.redis.zrevrange(
      sessionEventsKey, 
      0, 
      limit ? limit - 1 : -1
    );
    
    if (eventIds.length === 0) {
      return [];
    }
    
    // Fetch event data
    const events: AgentDetectionEvent[] = [];
    for (const eventId of eventIds) {
      const eventKey = this.getKey('event', eventId);
      const eventData = await this.redis.get(eventKey);
      if (eventData) {
        events.push(JSON.parse(eventData));
      }
    }
    
    return events;
  }
  
  async getSession(sessionId: string): Promise<AgentSession | null> {
    const sessionKey = this.getKey('session', sessionId);
    const sessionData = await this.redis.get(sessionKey);
    
    if (!sessionData) {
      return null;
    }
    
    return JSON.parse(sessionData);
  }
  
  async getRecentEvents(limit: number = 50): Promise<AgentDetectionEvent[]> {
    const timelineKey = `${this.prefix}timeline`;
    
    // Get recent event IDs
    const eventIds = await this.redis.zrevrange(timelineKey, 0, limit - 1);
    
    if (eventIds.length === 0) {
      return [];
    }
    
    // Fetch event data
    const events: AgentDetectionEvent[] = [];
    for (const eventId of eventIds) {
      if (eventId === 'cleanup') continue; // Skip cleanup marker
      
      const eventKey = this.getKey('event', eventId);
      const eventData = await this.redis.get(eventKey);
      if (eventData) {
        events.push(JSON.parse(eventData));
      }
    }
    
    return events;
  }
  
  async getActiveSessions(limit: number = 50): Promise<AgentSession[]> {
    const activeSessionsKey = `${this.prefix}active_sessions`;
    
    // Get recent session IDs
    const sessionIds = await this.redis.zrevrange(activeSessionsKey, 0, limit - 1);
    
    if (sessionIds.length === 0) {
      return [];
    }
    
    // Fetch session data
    const sessions: AgentSession[] = [];
    for (const sessionId of sessionIds) {
      const sessionKey = this.getKey('session', sessionId);
      const sessionData = await this.redis.get(sessionKey);
      if (sessionData) {
        sessions.push(JSON.parse(sessionData));
      }
    }
    
    return sessions;
  }
  
  async cleanup(before: Date): Promise<void> {
    // Redis handles TTL automatically, but we can clean up sorted sets
    const cutoff = before.getTime();
    const timelineKey = `${this.prefix}timeline`;
    const activeSessionsKey = `${this.prefix}active_sessions`;
    
    // Remove old entries from timeline
    await this.redis.zadd(timelineKey, cutoff, 'cleanup');
    
    // Remove old entries from active sessions
    await this.redis.zadd(activeSessionsKey, cutoff, 'cleanup');
  }
}